import React, { useMemo, useState, useEffect } from 'react'
import { SlidersHorizontal, Info, EyeOff } from 'lucide-react'
import { computePortfolio } from '../utils/portfolio'
import { formatCurrency, formatPrice, formatPercent, formatNumber } from '../utils/format'
import { toneColor } from '../utils/tone'

export default function LeverageSimulator({ price, leverage, portfolio, locked = false }) {
  const [debt, setDebt] = useState(leverage?.currentDebt ?? 0)

  useEffect(() => {
    if (typeof leverage?.currentDebt === 'number') setDebt(leverage.currentDebt)
  }, [leverage?.currentDebt])

  const metrics = useMemo(
    () => (portfolio ? computePortfolio({ ...portfolio, marginDebt: debt }, { price }) : null),
    [debt, price, portfolio]
  )

  if (locked || !portfolio || !metrics || typeof leverage?.currentDebt !== 'number') return <LockedSimulator />

  const extraShares = price > 0 ? Math.max(0, (debt - leverage.currentDebt) / price) : 0
  const headroomTone = metrics.marginHeadroom > 0.35 ? 'bull' : metrics.marginHeadroom > 0.2 ? 'caution' : 'bear'
  const recPct = pctOfRange(leverage.recommendedDebt, leverage.currentDebt, leverage.maxDebt)

  return (
    <div className="onds-card p-5 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
        <h2 className="text-lg font-bold">Leverage Simulator</h2>
      </div>
      <p className="text-xs text-[var(--onds-text-dim)] mb-5">
        Model additional margin. The committee authorizes more leverage only to press a winning trend.
      </p>

      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm text-[var(--onds-text-dim)]">Total margin debt</span>
        <span className="text-xl font-bold tabular-nums">{formatCurrency(debt)}</span>
      </div>

      <div className="relative mb-1">
        <input
          type="range"
          min={leverage.currentDebt}
          max={leverage.maxDebt}
          step={10000}
          value={debt}
          onChange={(e) => setDebt(Number(e.target.value))}
          className="w-full accent-indigo-500"
        />
        <div
          className="absolute -top-1 text-[10px] font-semibold px-1 rounded -translate-x-1/2 pointer-events-none"
          style={{ left: `${recPct}%`, color: 'var(--onds-accent)' }}
          title="Committee recommended cap"
        >
          ▲ rec
        </div>
      </div>
      <div className="flex justify-between text-[11px] text-[var(--onds-text-dim)] mb-5">
        <span>Now {formatCurrency(leverage.currentDebt)}</span>
        <span>Max {formatCurrency(leverage.maxDebt)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Cell label="Margin-Call Price" value={formatPrice(metrics.marginCallPrice)} tone={headroomTone} />
        <Cell label="Buffer" value={formatPercent(metrics.marginHeadroom, 0)} tone={headroomTone} />
        <Cell label="Extra Shares Buyable" value={formatNumber(Math.round(extraShares))} />
        <Cell label="Net If Liquidated" value={formatCurrency(metrics.netWorth)} />
      </div>

      {debt > leverage.recommendedDebt && (
        <div className="flex gap-2 text-xs rounded-lg p-3 mb-3" style={{ backgroundColor: 'var(--onds-bg)', color: toneColor('bear') }}>
          <Info className="h-4 w-4 shrink-0" />
          Above the committee's recommended cap of {formatCurrency(leverage.recommendedDebt)}. Buffer thinning; only justified on a confirmed catalyst.
        </div>
      )}

      <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: 'var(--onds-surface)', border: '1px dashed var(--onds-border)' }}>
        <div className="font-semibold mb-1" style={{ color: 'var(--onds-accent)' }}>Authorization trigger</div>
        <p className="text-[var(--onds-text-dim)]">{leverage.triggerCondition}</p>
      </div>
    </div>
  )
}

function LockedSimulator() {
  return (
    <div className="onds-card p-5 md:p-6">
      <div className="flex items-center gap-2 mb-1">
        <SlidersHorizontal className="h-5 w-5" style={{ color: 'var(--onds-accent)' }} />
        <h2 className="text-lg font-bold">Leverage Simulator</h2>
      </div>
      <div className="flex flex-col items-center justify-center text-center gap-2 py-10">
        <EyeOff className="h-6 w-6" style={{ color: 'var(--onds-text-dim)' }} />
        <p className="text-sm text-[var(--onds-text-dim)]">Sensitive position numbers are hidden.</p>
        <p className="text-xs text-[var(--onds-text-dim)]">Use the eye toggle at the top to reveal and simulate leverage.</p>
      </div>
    </div>
  )
}

function pctOfRange(value, min, max) {
  if (max <= min) return 0
  return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100))
}

function Cell({ label, value, tone = 'neutral' }) {
  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--onds-surface)' }}>
      <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">{label}</div>
      <div className="text-base font-bold tabular-nums mt-0.5" style={{ color: toneColor(tone) }}>{value}</div>
    </div>
  )
}
